import { ClassGroup } from 'modules/arena/class-groups/types';
import { ENROLLMENT_STATUS_LABELS } from 'modules/arena/class-groups/content';
import { Card } from 'shared/components/ui/Card';
import { Badge } from 'shared/components/ui/Badge';
import { Button } from 'shared/components/ui/Button';
import { Avatar } from 'shared/components/ui/Avatar';
import { AttendanceSessionCard } from 'modules/arena/class-groups/components/AttendanceSessionCard';
import { RENEWAL_MODE_LABELS } from 'shared/constants/labels';

interface ClassGroupDetailPanelProps {
  group: ClassGroup;
  expandedSessionId: string | null;
  onToggleExpandSession: (sessionId: string) => void;
  onTogglePresence: (sessionId: string, studentId: string) => void;
  onSaveAttendance: (sessionId: string) => void;
  onToggleGroupStatus?: (groupId: string) => void;
  /** Instrutores só consultam turmas/alunos e registram presença — não administram a turma. */
  canManage?: boolean;
}

const ENROLLMENT_TONE: Record<string, 'success' | 'warning' | 'danger'> = {
  ativa: 'success',
  pendente_renovacao: 'warning',
  cancelada: 'danger',
};

export function ClassGroupDetailPanel({
  group,
  expandedSessionId,
  onToggleExpandSession,
  onTogglePresence,
  onSaveAttendance,
  onToggleGroupStatus,
  canManage = true,
}: ClassGroupDetailPanelProps) {
  const activeStudents = group.students.filter((student) => student.status !== 'cancelada');

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">Turma</p>
          <h3 className="mt-1 font-heading text-lg font-semibold text-ink">{group.name}</h3>
          <p className="text-sm text-ink-secondary">
            {group.sport} · {group.instructor}
          </p>
          <p className="text-sm text-ink-secondary">
            {group.court} · {group.scheduleLabel} ({group.durationLabel})
          </p>
        </div>
        <Badge tone={group.status === 'ativa' ? 'success' : 'neutral'}>
          {group.status === 'ativa' ? 'Ativa' : 'Pausada'}
        </Badge>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs text-ink-secondary">Valor mensal</p>
          <p className="font-medium text-ink">R$ {group.monthlyPrice}</p>
        </div>
        <div>
          <p className="text-xs text-ink-secondary">Renovação</p>
          <p className="font-medium text-ink">{RENEWAL_MODE_LABELS[group.renewalMode]}</p>
        </div>
        <div className="col-span-2">
          <p className="text-xs text-ink-secondary">Política de cancelamento / reposição</p>
          <p className="font-medium text-ink">{group.cancellationPolicySnapshot}</p>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
          Alunos matriculados ({activeStudents.length}/{group.capacity})
        </p>
        <div className="mt-2 flex flex-col gap-2">
          {group.students.map((student) => (
            <div key={student.id} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Avatar name={student.name} size="sm" />
                <span className="text-ink">{student.name}</span>
              </div>
              <Badge tone={ENROLLMENT_TONE[student.status]}>{ENROLLMENT_STATUS_LABELS[student.status]}</Badge>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
          Próximas sessões
        </p>
        <div className="mt-2 flex flex-col gap-2">
          {group.upcomingSessions.map((session) => (
            <AttendanceSessionCard
              key={session.id}
              session={session}
              students={activeStudents}
              isExpanded={expandedSessionId === session.id}
              onToggleExpand={() => onToggleExpandSession(session.id)}
              onTogglePresence={(studentId) => onTogglePresence(session.id, studentId)}
              onSave={() => onSaveAttendance(session.id)}
            />
          ))}
        </div>
      </div>

      {canManage && onToggleGroupStatus && (
        <Button variant="ghost" className="mt-5 w-full" onClick={() => onToggleGroupStatus(group.id)}>
          {group.status === 'ativa' ? 'Pausar turma' : 'Reativar turma'}
        </Button>
      )}
    </Card>
  );
}
